import classNames from 'classnames';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CloseButton, Heading, Modal, Text } from '@wix/design-system';
import { PackagePickerType, Theme } from '../../../enums';
import { RootStore, withInjectedRootStore } from '../../../providers';
import s from './NoWebsiteModal.scss';

interface NoWebsiteModalProps {
  isOpen: boolean;
  onClose(): void;
  rootStore: RootStore;
}

const templatesPageURL: Partial<Record<PackagePickerType, string>> = {
  [PackagePickerType.Default]: '/website/templates',
  [PackagePickerType.Sunrise]: '/website/templates',
  [PackagePickerType.EditorX]: 'https://www.editorx.com/templates',
  [PackagePickerType.Dayful]: 'https://www.wix.com/intro/dayful',
  [PackagePickerType.Studio]: 'https://www.wix.com/studio/templates',
  [PackagePickerType.EditorXToStudio]: 'https://www.wix.com/studio/templates',
  [PackagePickerType.Sandbox]: 'https://www.wix.com/studio/templates',
};

const NoWebsiteModalComp: React.FC<NoWebsiteModalProps> = ({
  isOpen,
  onClose,
  rootStore,
}) => {
  const handleConfirmation = () => {
    const { packagePickerType } = rootStore.appStore.appContext;
    const redirectUrl =
      templatesPageURL[packagePickerType] ??
      templatesPageURL[PackagePickerType.Default]!;
    window.location.assign(redirectUrl);
  };
  return (
    <Modal dataHook='no-website-modal' isOpen={isOpen}>
      <ModalContent onClose={onClose} onConfirm={handleConfirmation} />
    </Modal>
  );
};

export interface ModalContentProps {
  onClose(): void;
  onConfirm(): void;
}

export const ModalContent: React.FC<ModalContentProps> = ({
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const { theme: themeName } = usePageModeContext();
  return (
    <div className={s.modalContainer}>
      <div className={s.header}>
        <CloseButton
          onClick={onClose}
          size='large'
          skin='dark'
          dataHook='close-button'
        />
      </div>
      <div className={s.modalBody}>
        <div className={classNames(s.image, themeName)}></div>
        <Heading
          dataHook='no-website-modal-title'
          appearance='H2'
          className={s.title}
        >
          {t(
            themeName === Theme.Dayful
              ? 'dayful.no_sites_modal.title'
              : 'no_sites_modal.title',
          )}
        </Heading>
        <Text dataHook='no-website-modal-subtitle'>
          {t(
            themeName === Theme.EditorX
              ? 'editorx.no_sites_modal.subtitle'
              : themeName === Theme.Dayful
              ? 'dayful.no_sites_modal.subtitle'
              : 'no_sites_modal.subtitle',
          )}
        </Text>
        <Button
          onClick={onConfirm}
          dataHook='confirm-button'
          data-testid='confirm-button'
          className={s.btn}
        >
          {t('no_sites_modal.cta')}
        </Button>
      </div>
    </div>
  );
};

export const NoWebsiteModal = withInjectedRootStore(NoWebsiteModalComp);
